/**
 * «Карман уровня» — вопрос об ЭТОМ экране, не уходя с него.
 *
 * Устройство держит четыре правила, и каждое из них — ответ на конкретную
 * ошибку, которую иначе сделал бы каждый экран по-своему.
 *
 * * **карман живёт в раскрытии и НЕ добавляется к первому взгляду страницы.**
 *   Экран уровня уже израсходовал свой бюджет плотности на ответ «что сделано,
 *   а что нет»; каталог инструментов, разложенный рядом, отнял бы у этого
 *   ответа место. Внутри кармана бюджет СВОЙ — те же четыре, пять и восемь, но
 *   считаемые заново;
 * * **область и уровень берутся из сессии и адреса, а не из полей.** Уровень
 *   задаёт экран, объект — его адрес: `object_id` шлюза — это то, НА ЧЁМ стоит
 *   экран, а не полномочие;
 * * **обещать можно только то, что сервер сдержит.** Инструмент без данных на
 *   срезе (`readiness: missing`) не запускается вовсе, и причина названа
 *   словами; дорогой инструмент запускается с оговоркой о времени; пустая
 *   область печатает свою статью вместо списка кнопок под отказ;
 * * **аноним получает одну строку, а не пустоту.** Чтение района открыто, а
 *   шлюз требует сессии: сказать об этом обязан карман, иначе человек видит
 *   молчащий блок и считает его поломкой.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, failureKind } from "../../api/client";
import type {
  AiEnvelope,
  AiToolItem,
  AiToolParam,
  AiToolCatalog,
  CampaignList,
} from "../../api/client";
import { Disclosure } from "../../components/Disclosure";
import { Select } from "../../components/Select";
import { Badge, Button, DataStatePanel } from "../../components/ui";
import { useAnalysisContext } from "../../analysis/context";
import { ownActorId, roleKind, useAuth } from "../../auth/context";
import { DENSITY, levelPath, type LevelId } from "../../lib/levels";
import { AnswerSpecView, readinessLabel } from "./AnswerSpecView";
import { splitByBudget } from "./answerModel";
import { emptyByLevel, emptyReasonWords } from "./contract";
import styles from "./ai.module.css";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { COMMON } from "../../i18n/common";
import { levelWord } from "./levelWords";

const m = defineMessages({
  isolationNote: {
    id: "answer.pocket.isolation.note",
    defaultMessage: "Область чтения применяется сервером: чужие хозяйства в счёт не входят.",
    description: "Пояснение в кармане «Спросить об этом уровне»: сервер сам ограничивает данные областью пользователя",
  },
  isolationLogin: {
    id: "answer.pocket.isolation.loginRequired",
    defaultMessage: "На этом сервере чтение закрыто до входа: без входа ответов не будет.",
    description: "Пояснение в кармане «Спросить об этом уровне»: чтение данных требует входа в систему",
  },
  title: {
    id: "answer.pocket.title",
    defaultMessage: "Спросить об этом уровне",
    description: "Заголовок раскрывающегося кармана ИИ на экранах уровней (район, хозяйство, контур…); все роли",
  },
  summaryClosed: {
    id: "answer.pocket.summary.closed",
    defaultMessage: "каталог инструментов уровня",
    description: "Подпись-сводка свёрнутого кармана ИИ, пока каталог не загружен",
  },
  summaryCount: {
    id: "answer.pocket.summary.count",
    defaultMessage: "инструментов: {count}",
    description: "Подпись-сводка раскрытого кармана ИИ; {count} — сколько инструментов (вопросов) доступно",
  },
  loginNeeded: {
    id: "answer.pocket.loginNeeded",
    defaultMessage: "Аналитика доступна после входа.",
    description: "Заметка в кармане ИИ для гостя: без входа аналитика недоступна",
  },
  catalogFailed: {
    id: "answer.pocket.catalogFailed",
    defaultMessage: "Каталог инструментов не загрузился",
    description: "Заголовок ошибки загрузки списка вопросов к ИИ в кармане; ниже — текст ошибки сервера",
  },
  catalogLoading: {
    id: "answer.pocket.catalogLoading",
    defaultMessage: "Смотрим, что можно спросить",
    description: "Заголовок индикатора загрузки списка вопросов к ИИ в кармане",
  },
  noToolsTitle: {
    id: "answer.pocket.noTools.title",
    defaultMessage: "Инструментов этого уровня нет",
    description: "Заголовок пустого списка вопросов к ИИ на этом уровне",
  },
  noToolsText: {
    id: "answer.pocket.noTools.text",
    defaultMessage: "Реестр не объявляет ни одного инструмента для вашей области.",
    description: "Пояснение к пустому списку вопросов к ИИ: для области пользователя ничего не объявлено",
  },
  moreTitle: {
    id: "answer.pocket.more.title",
    defaultMessage: "Остальные инструменты",
    description: "Заголовок вложенного раскрытия с остальными вопросами к ИИ в кармане",
  },
  moreSummary: {
    id: "answer.pocket.more.summary",
    defaultMessage: "ещё {count}",
    description: "Подпись-сводка раскрытия «Остальные инструменты»; {count} — сколько скрыто; коротко",
  },
  moreAction: {
    id: "answer.pocket.more.action",
    defaultMessage: "показать ещё {count}",
    description: "Кнопка раскрытия остальных вопросов к ИИ; {count} — сколько скрыто; коротко",
  },
  readinessHint: {
    id: "answer.tool.readiness.hint",
    defaultMessage: "Готовность данных на срезе, а не качество инструмента",
    description: "Подсказка у бейджа готовности данных инструмента ИИ (срез — снимок данных на дату)",
  },
  slowHint: {
    id: "answer.tool.slow.hint",
    defaultMessage: "Ответ считается дольше обычного",
    description: "Подсказка у бейджа «ответ дольше обычного» у инструмента ИИ",
  },
  slow: {
    id: "answer.tool.slow.badge",
    defaultMessage: "ответ дольше обычного",
    description: "Бейдж у инструмента ИИ: расчёт идёт дольше обычного; коротко",
  },
  slowAbout: {
    id: "answer.tool.slow.badgeAbout",
    defaultMessage: "ответ дольше обычного: около {seconds} с",
    description: "Бейдж у инструмента ИИ; {seconds} — примерное время расчёта в секундах (число), «с» — секунды",
  },
  noData: {
    id: "answer.tool.noData",
    defaultMessage: "Данных этого рода на срезе не поступало — спрашивать нечего. Это не ноль: строк в корпусе нет.",
    description: "Заметка у инструмента ИИ: по этому виду данных на выбранную дату ничего нет; «корпус» — набор данных системы",
  },
  objectMissing: {
    id: "answer.tool.objectMissing",
    defaultMessage: "Этот уровень считает по одной единице, а её не задаёт ни этот экран, ни ваша область: спросите на экране — <link>{level}</link>.",
    description: "Заметка у инструмента ИИ: нужен конкретный объект; <link> — ссылка на экран уровня, {level} — название уровня строчными (район, хозяйство, контур)",
  },
  unfillablePocket: {
    id: "answer.tool.unfillable.pocket",
    defaultMessage: "Инструмент просит назвать объект, которого на этом экране не выбирают: {params}. Спросите его на станции ответов.",
    description: "Заметка у инструмента ИИ в кармане; {params} — названия параметров через запятую; «станция ответов» — отдельный раздел аналитики",
  },
  unfillableStation: {
    id: "answer.tool.unfillable.station",
    defaultMessage: "Инструмент просит назвать объект, которого на этом экране не выбирают: {params}. Его спрашивают на экране самой единицы — <link>{level}</link>.",
    description: "Заметка у инструмента ИИ на станции ответов; {params} — названия параметров, <link> — ссылка на экран уровня, {level} — название уровня строчными",
  },
  brokenTitle: {
    id: "answer.tool.broken.title",
    defaultMessage: "Справочник кампаний не загрузился",
    description: "Заголовок ошибки: список кампаний для параметра инструмента ИИ не загрузился",
  },
  brokenText: {
    id: "answer.tool.broken.text",
    defaultMessage: "Значения для {params} есть, но список не довезли: это сбой запроса, а не «кампаний нет».",
    description: "Пояснение к ошибке справочника кампаний; {params} — названия параметров через запятую",
  },
  fixed: {
    id: "answer.tool.param.fixed",
    defaultMessage: "{label}: объект этого экрана (#{id})",
    description: "Подпись параметра инструмента ИИ, который задаёт сам экран; {label} — название параметра, {id} — номер объекта",
  },
  paramLoading: {
    id: "answer.tool.param.loading",
    defaultMessage: "{label}: справочник загружается",
    description: "Подпись параметра инструмента ИИ, пока справочник вариантов загружается; {label} — название параметра",
  },
  asking: {
    id: "answer.tool.asking",
    defaultMessage: "Считаем…",
    description: "Текст кнопки «Спросить» на время расчёта; коротко",
  },
  ask: {
    id: "answer.tool.ask",
    defaultMessage: "Спросить",
    description: "Кнопка запуска вопроса к ИИ в карточке инструмента; коротко",
  },
  loginTitle: {
    id: "answer.tool.error.login",
    defaultMessage: "Нужен вход",
    description: "Заголовок панели: ответ ИИ недоступен без входа",
  },
  failedTitle: {
    id: "answer.tool.error.failed",
    defaultMessage: "Ответ не получен",
    description: "Заголовок панели ошибки: ответ ИИ получить не удалось; ниже — текст ошибки",
  },
});

/** Что объявляет каталог про изоляцию чтения. Тоже код, тоже словарь клиента. */
const ISOLATION_NOTE: Record<string, PlainMessage> = {
  "serving.read_scope.note.v1": m.isolationNote,
  "serving.read_scope.login_required.v1": m.isolationLogin,
};

/**
 * Состояние справочника кампаний — ЧЕТЫРЕ исхода, не три.
 *
 * `null` — запрос в пути; список — пришёл (возможно пустой, и это «кампаний
 * нет»); `"failed"` — не загрузился. Последнее отдельно от первого намеренно:
 * сложить сбой в «ещё в пути» значит печатать слово «загружается» вечно.
 */
export type CampaignsState = CampaignList | "failed" | null;

/** Единица, которой адресуется уровень, — для параметров вида «ссылка». */
const LEVEL_ENTITY: Record<LevelId, string> = {
  district: "district",
  massif: "massif",
  actor: "actor",
  contour: "contour",
  work: "work",
};

export function LevelPocket({
  level,
  objectId = null,
  title,
}: {
  level: LevelId;
  /** Объект, НА КОТОРОМ стоит экран. `null` — уровень без своей единицы. */
  objectId?: number | null;
  title?: string;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [opened, setOpened] = useState(false);
  const [catalog, setCatalog] = useState<AiToolCatalog | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignsState>(null);
  // Запрос в пути. Нужен ровно затем, чтобы повторное раскрытие после отказа
  // спрашивало заново, но два жеста подряд не давали двух запросов: без него
  // «сбросить ошибку и спросить снова» превращается в дребезг.
  const inFlight = useRef(false);
  // Раскрыт ли карман — РЕФОМ, а не зависимостью эффекта. Отдай `opened` в
  // зависимости сброса — и каждый жест раскрытия стирал бы только что
  // полученный каталог, то есть запрос шёл бы за запросом.
  const opening = useRef(false);
  // Поколение среза. Ответ, уехавший до смены среза, приезжает ПОСЛЕ неё — и
  // без этого счётчика лёг бы каталогом под новую дату, то есть показал бы
  // вчерашнюю готовность рядом с сегодняшним числом.
  const generation = useRef(0);

  // Справочник кампаний вынесен отдельно: у него ЧЕТЫРЕ исхода, и «не
  // загрузился» обязан отличаться и от «ещё в пути», и от «кампаний нет».
  // Иначе отказ сети навсегда печатается словом «загружается», а человек
  // ждёт того, что уже не придёт.
  const loadCampaigns = useCallback(() => {
    setCampaigns(null);
    api
      .campaigns(context.params)
      .then(setCampaigns)
      .catch(() => setCampaigns("failed"));
  }, [context.params]);

  // Сам запрос каталога — без решения «а надо ли». Решают два вызывающих:
  // жест раскрытия и смена среза, и условия у них разные.
  const fetchCatalog = useCallback(() => {
    const mine = generation.current;
    inFlight.current = true;
    setError(null);
    api
      .aiTools({ ...context.params, level, scope_id: objectId ?? undefined })
      .then((data) => {
        if (mine !== generation.current) return;
        setCatalog(data);
        // Кампании нужны единственному параметру-ссылке реестра
        // (`campaign_id`): без справочника его нечем заполнить, а угадывать
        // номер за человека нельзя.
        if (needsCampaign(data)) loadCampaigns();
      })
      .catch((cause: unknown) => {
        if (mine !== generation.current) return;
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError("unknown_error", String(cause), 0),
        );
      })
      .finally(() => {
        if (mine === generation.current) inFlight.current = false;
      });
  }, [context.params, level, loadCampaigns, objectId]);

  // Срез поменялся — каталог протух: готовность считается НА СРЕЗЕ, и
  // показывать вчерашнюю рядом с сегодняшней датой значит врать о данных.
  //
  // Сброса ОДНОГО тут мало, и это стоило отдельной правки. Раскрытие
  // управляемое (`open={opened}`), при смене среза карман остаётся раскрытым,
  // переключатель не срабатывает — а запрос инициировал только он. Значит
  // сброшенный каталог никто бы не запросил, и панель «Смотрим, что можно
  // спросить» висела бы, пока человек не свернёт и не раскроет карман руками.
  // Жест этот массовый: переключатель демо и поле даты стоят в шапке КАЖДОГО
  // экрана. Поэтому раскрытый карман спрашивает заново прямо здесь, а
  // свёрнутый по-прежнему не стоит ни одного запроса.
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setCatalog(null);
    setError(null);
    setCampaigns(null);
    if (opening.current) fetchCatalog();
  }, [context.key, fetchCatalog]);

  // Каталог запрашивается ТОЛЬКО после раскрытия: свёрнутый карман не должен
  // стоить экрану ни одного запроса, иначе «свернули» перестанет означать
  // «не спрашивали». А вот ПОВТОРНОЕ раскрытие после отказа обязано спросить
  // снова: это единственный жест починки, доступный человеку при сбое сети.
  const load = useCallback(
    (open: boolean) => {
      opening.current = open;
      setOpened(open);
      if (!open || catalog || inFlight.current) return;
      fetchCatalog();
    },
    [catalog, fetchCatalog],
  );

  const body = () => {
    if (error) {
      if (failureKind(error) === "unauthenticated") {
        return <p className={styles.note}>{t(m.loginNeeded)}</p>;
      }
      return (
        <DataStatePanel state="error" title={t(m.catalogFailed)}>
          {error.message}
        </DataStatePanel>
      );
    }
    if (!catalog) {
      return <DataStatePanel state="loading" title={t(m.catalogLoading)} />;
    }
    if (catalog.empty_reason) {
      // Пустота по УРОВНЮ и пустота по ОБЛАСТИ — разные утверждения, и
      // различает их сервер статьёй словаря. «Инструментов уровня «работа»
      // пока нет» чинится переходом на экран выше; «считать не за кого» не
      // чинится ничем, и сводить их в одну фразу значит отнять у человека
      // единственную подсказку, что делать дальше. Уровень пустоты —
      // ЗАПРОШЕННЫЙ (эхо каталога), а не уровень компонента: спросили одно,
      // ответили о другом — это разные вещи, и печатать надо ответ.
      const words = emptyReasonWords(
        catalog.empty_reason,
        catalog.requested_level ?? level,
      );
      return (
        <DataStatePanel
          state={emptyByLevel(catalog) ? "empty" : "blocked"}
          title={words.title}
        >
          {words.text}
        </DataStatePanel>
      );
    }
    const tools = catalog.tools ?? [];
    if (tools.length === 0) {
      return (
        <DataStatePanel state="empty" title={t(m.noToolsTitle)}>
          {t(m.noToolsText)}
        </DataStatePanel>
      );
    }
    // Бюджет плотности у кармана СВОЙ: он вложенный экран, а не довесок к
    // первому взгляду страницы. Шесть инструментов района, разложенных
    // подряд, — это шесть карточек с селектами и кнопками, то есть ровно тот
    // список, который бюджет и запрещает.
    const { shown, hidden } = splitByBudget(tools, DENSITY.preview);
    const row = (tool: AiToolItem) => (
      <ToolRow
        key={tool.tool_id}
        tool={tool}
        objectId={objectId}
        level={level}
        campaigns={campaigns}
        onRetryCampaigns={loadCampaigns}
        surface="pocket"
      />
    );
    return (
      <div className={styles.pocket}>
        <p className={styles.note}>
          {ISOLATION_NOTE[catalog.isolation_note]
            ? t(ISOLATION_NOTE[catalog.isolation_note]!)
            : catalog.isolation_note}
        </p>
        {shown.map(row)}
        {hidden.length > 0 ? (
          <Disclosure
            tone="inline"
            title={t(m.moreTitle)}
            summary={t(m.moreSummary, { count: hidden.length })}
            more={t(m.moreAction, { count: hidden.length })}
          >
            <div className={styles.pocket}>{hidden.map(row)}</div>
          </Disclosure>
        ) : null}
      </div>
    );
  };

  return (
    <Disclosure
      title={title ?? t(m.title)}
      summary={
        catalog
          ? t(m.summaryCount, { count: (catalog.tools ?? []).length })
          : t(m.summaryClosed)
      }
      open={opened}
      onToggle={load}
    >
      {body()}
    </Disclosure>
  );
}

function needsCampaign(catalog: AiToolCatalog): boolean {
  return (catalog.tools ?? []).some((tool) =>
    (tool.params ?? []).some(
      (param) => param.kind === "id" && param.entity === "campaign",
    ),
  );
}

/* ------------------------------------------------------------------ */
/* Один инструмент                                                     */
/* ------------------------------------------------------------------ */

/**
 * Строка каталога: что спросить, чем это заполнить и что из этого вышло.
 *
 * Экспортируется ради станции ответов: пресет на станции и пресет в кармане —
 * один и тот же вопрос, и рисовать их двумя компонентами значило бы завести
 * два набора правил запуска (готовность, цена, недостающий параметр).
 */
export function ToolRow({
  tool,
  objectId,
  level,
  campaigns,
  onRetryCampaigns,
  surface,
}: {
  tool: AiToolItem;
  objectId: number | null;
  level: LevelId;
  campaigns: CampaignsState;
  /** Спросить справочник заново после сбоя. Без него сбой — тупик. */
  onRetryCampaigns?: () => void;
  /**
   * Где нарисована строка. От этого зависит ОДНА фраза — куда отсылать за
   * параметром, которого здесь не выбирают: на станции «спросите на станции»
   * было бы советом пойти туда, где человек уже стоит.
   */
  surface: "pocket" | "station";
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [values, setValues] = useState<Record<string, string>>({});
  const [envelope, setEnvelope] = useState<AiEnvelope | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [asking, setAsking] = useState(false);

  // Уровень ниже района ЗОВЁТ единицу: селектор массива/хозяйства считает по
  // объекту, а объект едет отдельным полем `object_id`, не параметром
  // инструмента (Решение Д11). По списку `params` эта недостача не видна
  // вовсе — значит её ищут отдельно.
  //
  // Но искать её В УРОВНЕ нельзя, и это стоило регрессии. Единица уровня
  // «хозяйство» бывает известна БЕЗ адреса экрана: у аккаунта хозяйства она
  // одна и та же — своё хозяйство, и сервер подставляет её сам
  // (`object_of` → `scope.scope_id`). Вывести «объекта нет» из одного лишь
  // уровня значило бы выключить фермеру на станции ровно те два вопроса,
  // ради которых он туда заходит, — и сказать ему неправду: сервер этот
  // вызов обслуживает.
  //
  // Число называется ЯВНО, а не оставляется молчаливой подстановке сервера:
  // так в теле запроса видно, о ком спрашивали, и так же заполняется
  // параметр-ссылка на хозяйство. Источник тот же, которым весь кабинет
  // называет «своё хозяйство», — область из сессии, не из полей экрана.
  const auth = useAuth();
  const ownActor = ownActorId(auth.principal);
  const object = objectId ?? (level === "actor" ? ownActor : null);
  // Взять объект НЕОТКУДА — ни из адреса экрана, ни из области участника:
  // вот это и есть несобираемый вызов. Обещать можно только то, что сервер
  // сдержит.
  const objectMissing = level !== "district" && object === null;

  const params = useMemo(() => tool.params ?? [], [tool.params]);
  // Параметр-ссылка, который нечем заполнить, — это не «пустое поле», а
  // невозможность корректного вызова: кнопка не должна обещать ответ.
  //
  // «Ещё не загрузили справочник» и «взять значения неоткуда» здесь
  // РАЗЛИЧАЮТСЯ: пока справочник в пути, объявлять вызов несобираемым значило
  // бы сказать человеку неправду на полсекунды — и он успел бы ей поверить.
  const pending = params.filter(
    (param) =>
      fixedValue(param, level, object) === null &&
      choices(param, campaigns) === undefined,
  );
  const unfillable = params.filter(
    (param) =>
      fixedValue(param, level, object) === null &&
      choices(param, campaigns) === null,
  );
  // Справочник не загрузился — это НЕ «взять неоткуда»: значения существуют,
  // их не довезли. Отсюда и другая фраза, и предложение повторить.
  const broken = params.filter(
    (param) =>
      fixedValue(param, level, object) === null &&
      choices(param, campaigns) === "failed",
  );
  const missingData = tool.readiness === "missing";
  const ready =
    !missingData &&
    !objectMissing &&
    unfillable.length === 0 &&
    pending.length === 0 &&
    broken.length === 0 &&
    params.every(
      (param) =>
        fixedValue(param, level, object) !== null || (values[param.name] ?? "") !== "",
    );

  const ask = () => {
    setAsking(true);
    setError(null);
    const body: Record<string, string | number> = {};
    for (const param of params) {
      const fixed = fixedValue(param, level, object);
      if (fixed !== null) {
        body[param.name] = fixed;
        continue;
      }
      const chosen = values[param.name] ?? "";
      body[param.name] = param.kind === "id" ? Number(chosen) : chosen;
    }
    api
      .aiAnswer(
        {
          tool_id: tool.tool_id,
          params: body,
          context: {
            as_of: context.asOf,
            include_demo: context.includeDemo,
          },
          // Объект — разрешённый адрес уровня (см. `object` выше): у участника
          // области хозяйства он берётся из самой области, а не из пропса.
          object_id: object,
          // Уровень называет ЭКРАН, на котором стоит строка каталога — см.
          // `api.aiAnswer`. Один и тот же инструмент объявлен на трёх уровнях
          // (Решение Д11), и без этого поля сервер выбирал бы селектор за
          // человека: из кармана хозяйства прилетел бы районный счёт, а
          // подпись рядом осталась бы «хозяйство». Полномочием поле не
          // является — область по-прежнему из сессии, и чужой уровень
          // отвечает отказом, а не расширением.
          level,
        },
        {},
      )
      .then(setEnvelope)
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError("unknown_error", String(cause), 0),
        ),
      )
      .finally(() => setAsking(false));
  };

  return (
    <div className={styles.tool}>
      <div className={styles.toolHead}>
        <span className={styles.toolId}>{tool.tool_id}</span>
        <Badge
          tone={
            tool.readiness === "factual"
              ? "source"
              : tool.readiness === "derived"
                ? "derived"
                : tool.readiness === "demo"
                  ? "demo"
                  : "unknown"
          }
          title={t(m.readinessHint)}
        >
          {readinessLabel(tool.readiness)}
        </Badge>
        {tool.cost === "slow" ? (
          // Цена — ОГОВОРКА, а не запрет: инструмент остаётся в каталоге, но
          // человек знает, что ждать придётся дольше обычного.
          <Badge tone="attention" title={t(m.slowHint)}>
            {tool.benchmark_ms
              ? t(m.slowAbout, { seconds: Math.round(tool.benchmark_ms / 1000) })
              : t(m.slow)}
          </Badge>
        ) : null}
      </div>

      {missingData ? (
        <p className={styles.note}>
          {t(m.noData)}
        </p>
      ) : null}

      {objectMissing ? (
        <p className={styles.note}>
          {t(m.objectMissing, {
            level: levelWord(level),
            link: (chunks) => (
              <Link to={{ pathname: levelPath(level), search: context.linkSearch() }}>
                {chunks}
              </Link>
            ),
          })}
        </p>
      ) : null}

      {unfillable.length > 0 ? (
        <p className={styles.note}>
          {surface === "pocket"
            ? t(m.unfillablePocket, {
                params: unfillable.map((param) => param.label).join(", "),
              })
            : t(m.unfillableStation, {
                params: unfillable.map((param) => param.label).join(", "),
                level: levelWord(level),
                link: (chunks) => (
                  <Link
                    to={{ pathname: levelPath(level), search: context.linkSearch() }}
                  >
                    {chunks}
                  </Link>
                ),
              })}
        </p>
      ) : null}

      {broken.length > 0 ? (
        <DataStatePanel state="error" title={t(m.brokenTitle)}>
          <p>
            {t(m.brokenText, {
              params: broken.map((param) => param.label).join(", "),
            })}
          </p>
          {onRetryCampaigns ? (
            <Button onClick={onRetryCampaigns}>{t(COMMON.retry)}</Button>
          ) : null}
        </DataStatePanel>
      ) : null}

      <div className={styles.toolParams}>
        {params.map((param) => {
          const fixed = fixedValue(param, level, object);
          if (fixed !== null) {
            return (
              <p key={param.name} className={styles.note}>
                {t(m.fixed, { label: param.label, id: fixed })}
              </p>
            );
          }
          const options = choices(param, campaigns);
          if (options === undefined) {
            return (
              <p key={param.name} className={styles.note}>
                {t(m.paramLoading, { label: param.label })}
              </p>
            );
          }
          // «Не загрузился» и «взять неоткуда» уже сказаны выше словами, и
          // пустой селект под ними был бы третьим утверждением о том же.
          if (options === null || options === "failed") return null;
          return (
            <Select
              key={param.name}
              size="sm"
              label={param.label}
              options={options}
              value={values[param.name] ?? ""}
              onChange={(next) =>
                setValues((previous) => ({ ...previous, [param.name]: next }))
              }
            />
          );
        })}
        <Button variant="primary" onClick={ask} disabled={!ready || asking}>
          {asking ? t(m.asking) : t(m.ask)}
        </Button>
      </div>

      {error ? (
        <DataStatePanel
          state={failureKind(error) === "unauthenticated" ? "blocked" : "error"}
          title={
            failureKind(error) === "unauthenticated"
              ? t(m.loginTitle)
              : t(m.failedTitle)
          }
        >
          {error.message}
        </DataStatePanel>
      ) : null}

      {envelope ? (
        <div className={styles.result}>
          <AnswerSpecView
            envelope={envelope}
            view={{
              linkSearch: context.linkSearch,
              // Область — ИЗ СЕССИИ, тем же источником, которым карман берёт
              // «своё хозяйство». В ответе её нет: `spec.scope.kind` говорит
              // про запись реестра уровня, а не про аккаунт.
              accountScope: roleKind(auth.principal),
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Значение параметра, которое задаёт САМ ЭКРАН.
 *
 * Ссылка на единицу этого уровня заполняется адресом: спрашивать у человека
 * номер контура на экране контура значило бы переспрашивать то, что и так
 * известно.
 */
function fixedValue(
  param: AiToolParam,
  level: LevelId,
  objectId: number | null,
): number | null {
  if (param.kind !== "id") return null;
  if (objectId === null) return null;
  return param.entity === LEVEL_ENTITY[level] ? objectId : null;
}

/**
 * Варианты параметра.
 *
 * Четыре исхода, и различать их обязательно: список вариантов; `null` —
 * «взять неоткуда», то есть вызов не собрать; `undefined` — «справочник ещё в
 * пути», и до его прихода никакого утверждения о невозможности делать нельзя;
 * `"failed"` — справочник не загрузился, то есть значения есть, но их не
 * довезли. Четвёртый исход заведён отдельно потому, что сложенный в третий он
 * печатает «загружается» до перезагрузки страницы.
 */
function choices(
  param: AiToolParam,
  campaigns: CampaignsState,
): { value: string; label: string }[] | null | undefined | "failed" {
  if (param.kind === "enum") {
    // Подпись значения — СЛОВАРНАЯ СТАТЬЯ РЕЕСТРА, доехавшая каталогом.
    // Своего словаря у клиента нет и не заводится: «week» — машинный ключ
    // закрытого набора, он уезжает в тело вызова и в журнал, и перевод его в
    // браузере был бы вторым словарём рядом с серверным. Значение без статьи
    // печатается ключом — сказать о нём неправду хуже, чем показать ключ, — но
    // до экрана такой случай не доходит: реестр без подписи не грузится.
    return (param.values ?? []).map((value) => ({
      value,
      label: param.value_labels?.[value] ?? value,
    }));
  }
  if (param.entity === "campaign") {
    if (campaigns === null) return undefined;
    if (campaigns === "failed") return "failed";
    const items = campaigns.items ?? [];
    if (items.length === 0) return null;
    return items.map((item) => ({
      value: String(item.id),
      label: item.title,
    }));
  }
  return null;
}
